import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  IconButton,
  Chip,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Refresh as RefreshIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';

/**
 * Controles para historial y rutas
 * Permite seleccionar rango de fechas o últimas N posiciones
 */
const HistoryControls = ({
  worker,
  onClose,
  onLoadHistory,
  isLoading
}) => {
  const [mode, setMode] = useState('lastPositions'); // 'lastPositions' | 'dateRange'
  const [lastPositions, setLastPositions] = useState(50);
  const [dateRange, setDateRange] = useState({
    fecha_inicio: getDateHoursAgo(24),
    fecha_fin: getCurrentDateTime()
  });
  const [error, setError] = useState(null);

  const handleLoadHistory = () => {
    setError(null);

    if (mode === 'lastPositions') {
      if (lastPositions < 1 || lastPositions > 1000) {
        setError('El número de posiciones debe estar entre 1 y 1000');
        return;
      }

      onLoadHistory({
        mode: 'lastPositions',
        limit: lastPositions,
        id_trabajador: worker.id_trabajador
      });
    } else {
      if (!dateRange.fecha_inicio || !dateRange.fecha_fin) {
        setError('Debe seleccionar un rango de fechas válido');
        return;
      }

      const inicio = new Date(dateRange.fecha_inicio);
      const fin = new Date(dateRange.fecha_fin);

      if (inicio >= fin) {
        setError('La fecha de inicio debe ser anterior a la fecha de fin');
        return;
      }

      const diffDays = (fin - inicio) / (1000 * 60 * 60 * 24);
      if (diffDays > 30) {
        setError('El rango de fechas no puede ser mayor a 30 días');
        return;
      }

      onLoadHistory({
        mode: 'dateRange',
        fecha_inicio: dateRange.fecha_inicio,
        fecha_fin: dateRange.fecha_fin,
        id_trabajador: worker.id_trabajador
      });
    }
  };

  const handleQuickRange = (hours) => {
    setMode('dateRange');
    setDateRange({
      fecha_inicio: getDateHoursAgo(hours),
      fecha_fin: getCurrentDateTime()
    });
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        bottom: 20,
        left: 20,
        width: 400,
        zIndex: 1000,
        borderRadius: 2
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">
            Historial de Ubicaciones
          </Typography>
          <Typography variant="caption">
            {worker.nombre_completo || `${worker.nombre} ${worker.apellido}`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 2 }}>
        {/* Modo de selección */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Modo de consulta</InputLabel>
          <Select
            value={mode}
            label="Modo de consulta"
            onChange={(e) => setMode(e.target.value)}
          >
            <MenuItem value="lastPositions">Últimas N posiciones</MenuItem>
            <MenuItem value="dateRange">Rango de fechas</MenuItem>
          </Select>
        </FormControl>

        {/* Últimas posiciones */}
        {mode === 'lastPositions' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Número de posiciones: {lastPositions}
            </Typography>
            <Slider
              value={lastPositions}
              onChange={(e, value) => setLastPositions(value)}
              min={10}
              max={500}
              step={10}
              marks={[
                { value: 10, label: '10' },
                { value: 100, label: '100' },
                { value: 250, label: '250' },
                { value: 500, label: '500' }
              ]}
              valueLabelDisplay="auto"
            />
            <TextField
              fullWidth
              type="number"
              label="Posiciones exactas"
              value={lastPositions}
              onChange={(e) => setLastPositions(parseInt(e.target.value) || 10)}
              inputProps={{ min: 1, max: 1000 }}
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>
        )}

        {/* Rango de fechas */}
        {mode === 'dateRange' && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label="Última hora"
                size="small"
                onClick={() => handleQuickRange(1)}
                variant="outlined"
              />
              <Chip
                label="Últimas 6 horas"
                size="small"
                onClick={() => handleQuickRange(6)}
                variant="outlined"
              />
              <Chip
                label="Últimas 24 horas"
                size="small"
                onClick={() => handleQuickRange(24)}
                variant="outlined"
              />
              <Chip
                label="Última semana"
                size="small"
                onClick={() => handleQuickRange(24 * 7)}
                variant="outlined"
              />
            </Box>

            <TextField
              fullWidth
              type="datetime-local"
              label="Fecha y hora de inicio"
              value={dateRange.fecha_inicio}
              onChange={(e) => setDateRange({ ...dateRange, fecha_inicio: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="datetime-local"
              label="Fecha y hora de fin"
              value={dateRange.fecha_fin}
              onChange={(e) => setDateRange({ ...dateRange, fecha_fin: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Botón cargar */}
        <Button
          fullWidth
          variant="contained"
          startIcon={isLoading ? <RefreshIcon className="spinning" /> : <PlayIcon />}
          onClick={handleLoadHistory}
          disabled={isLoading}
        >
          {isLoading ? 'Cargando...' : 'Cargar Historial'}
        </Button>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
          {mode === 'lastPositions'
            ? `Se cargarán las últimas ${lastPositions} posiciones`
            : `Se cargarán posiciones entre ${new Date(dateRange.fecha_inicio).toLocaleDateString()} y ${new Date(dateRange.fecha_fin).toLocaleDateString()}`
          }
        </Typography>
      </Box>

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spinning {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </Paper>
  );
};

/**
 * Obtener fecha/hora actual en formato datetime-local
 */
function getCurrentDateTime() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Obtener fecha/hora de hace N horas
 */
function getDateHoursAgo(hours) {
  const date = new Date();
  date.setHours(date.getHours() - hours);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hoursStr = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hoursStr}:${minutes}`;
}

export default HistoryControls;
